import { REPORT_COLOR_PRESETS } from '../consts';

export type ReportThemeId = 'lolly' | 'midnight' | 'brief';

export interface ReportThemeTokens {
	font: string | null;
	page: string;
	surface: string;
	border: string;
	divider: string;
	value: string;
	body: string;
	muted: string;
	chip: string;
	chartTick: string;
	chartGrid: string;
	chartLabel: string;
	coverGlowA: string;
	coverGlowB: string;
}

export interface ReportTheme {
	id: ReportThemeId;
	label: string;
	defaultAccent: string;
	accentPresets: string[];
	preview: { surface: string; text: string; font: string };
	// CONTRACT: null draws every slide in the literals it was designed with
	// (slides/_report-tokens.scss), so a Lolly report looks as it always has.
	tokens: ReportThemeTokens | null;
}

const SERIF_STACK = "'Source Serif 4', Georgia, 'Times New Roman', serif";

// LIMIT: Midnight's and Brief's values are proposed from the LOLLY report kit's
// own navy and ink, not taken from a design file — design sign-off is T-2407's.
export const REPORT_THEMES: ReadonlyArray<ReportTheme> = [
	{
		id: 'lolly',
		label: 'Lolly',
		defaultAccent: '#7e65ff',
		accentPresets: REPORT_COLOR_PRESETS.map(color => color.toLowerCase()),
		preview: { surface: '#ffffff', text: '#0e1a33', font: 'Gilroy' },
		tokens: null
	},
	{
		id: 'midnight',
		label: 'Midnight',
		defaultAccent: '#8f7bff',
		accentPresets: [
			'#8f7bff',
			'#6e8bff',
			'#ff6b74',
			'#ffa25c',
			'#2fd27a',
			'#e6ecf7'
		],
		preview: { surface: '#0e1a33', text: '#f4f7fc', font: 'Gilroy' },
		tokens: {
			font: null,
			page: '#0e1a33',
			surface: '#0e1a33',
			border: '#26365a',
			divider: '#1e2c4b',
			value: '#f4f7fc',
			body: '#c9d2e3',
			muted: '#8e9bb9',
			chip: '#1f2e4f',
			chartTick: '#a3b1cc',
			chartGrid: 'rgba(163, 177, 204, 0.18)',
			chartLabel: '#e6ecf7',
			coverGlowA: '#25407a',
			coverGlowB: '#4b2a5c'
		}
	},
	{
		id: 'brief',
		label: 'Brief',
		defaultAccent: '#1f4e79',
		accentPresets: [
			'#1f4e79',
			'#7e65ff',
			'#b3261e',
			'#c0692b',
			'#2e7d4f',
			'#1c1a17'
		],
		preview: { surface: '#fbf9f5', text: '#1c1a17', font: SERIF_STACK },
		tokens: {
			font: SERIF_STACK,
			page: '#fbf9f5',
			surface: '#fbf9f5',
			border: '#e4ded3',
			divider: '#ece7de',
			value: '#1c1a17',
			body: '#3b3833',
			muted: '#726c63',
			chip: '#efeae1',
			chartTick: '#7d776e',
			chartGrid: 'rgba(125, 119, 110, 0.18)',
			chartLabel: '#1c1a17',
			coverGlowA: '#e9dfcb',
			coverGlowB: '#f0dcd3'
		}
	}
];

export function reportTheme(id: string | null | undefined): ReportTheme {
	return REPORT_THEMES.find(theme => theme.id === id) ?? REPORT_THEMES[0];
}

export function isReportThemeId(value: unknown): value is ReportThemeId {
	return REPORT_THEMES.some(theme => theme.id === value);
}

// CONTRACT: the names slides/_report-tokens.scss and the chart theme plugin
// (report-chart-theme.ts) read, each with today's literal as its fallback.
export function reportThemeCssVars(
	theme: ReportTheme,
	accent: string | null
): Record<string, string> {
	const tokens = theme.tokens;
	if (!tokens) {
		return {};
	}
	const vars: Record<string, string> = {
		'--report-accent': accent ?? theme.defaultAccent,
		'--report-page': tokens.page,
		'--report-surface': tokens.surface,
		'--report-border': tokens.border,
		'--report-divider': tokens.divider,
		'--report-value': tokens.value,
		'--report-body': tokens.body,
		'--report-muted': tokens.muted,
		'--report-chip': tokens.chip,
		'--report-chart-tick': tokens.chartTick,
		'--report-chart-grid': tokens.chartGrid,
		'--report-chart-label': tokens.chartLabel,
		'--report-chart-surface': tokens.surface,
		'--report-cover-glow-a': tokens.coverGlowA,
		'--report-cover-glow-b': tokens.coverGlowB
	};
	if (tokens.font) {
		vars['--report-font'] = tokens.font;
		vars['--report-chart-font'] = tokens.font;
	}
	return vars;
}

// An accent left at the old theme's own default follows to the new theme's;
// one somebody chose — a brand kit's, the reader's — stays.
export function accentForTheme(
	accent: string | null,
	from: ReportTheme,
	to: ReportTheme
): string {
	return !accent || accent === from.defaultAccent ? to.defaultAccent : accent;
}
